import { api, type MusicCategory, type MusicInfo } from '../sync/api';
import { videoConvert } from '../video/engine';
import { fmtTime } from '../video/project';
import { shortText } from '../print/rules';
import { initAiMusic } from './aiMusic';
import { $, $in } from './dom';
import { openSheet } from './sheets';
import { toast } from './toast';

/*
 * "คลังเพลงของกลุ่ม" (0009), opened from "เสียง" on the video screen.
 *   everyone   listen, "ใช้เพลงนี้" (downloaded to this device like a song chosen from it, and the work remembers
 *              the library id, so other devices and templates fetch it again by themselves)
 *   0011       members given the right: "อัปโหลดเพลงเข้าคลัง" (canUpload) and/or "ให้ AI แต่งเพลง" (canAi, own drafts);
 *              everything else below stays with admins
 *   admins     upload (with the source and a tick that the group has the right to use it), add a category,
 *              "ซ่อน" (not offered any more, works that use it still play it) and "ลบจริง" (warned with how many
 *              works/templates still use it — choice ค), and "ให้ AI แต่งเพลง" (0010, src/ui/aiMusic.ts)
 */
export interface MusicHooks {
  /** put the song into the open video work; a Thai reason is thrown when it cannot */
  use: (m: MusicInfo) => Promise<void>;
  isAdmin: () => boolean;
  /** may upload songs (admin, or the right 'music_upload') */
  canUpload: () => boolean;
  /** may ask AI for songs (admin, or the right 'ai_music') */
  canAi: () => boolean;
  readonly: () => boolean;
  online: () => string | null;
  /** the open video's length in seconds (AI music starts at this length) */
  videoSeconds: () => number;
}

const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
const MAX_BYTES = 15 * 1024 * 1024;

export function initMusicLibrary(h: MusicHooks) {
  const list = $('mlList'), audio = $<HTMLAudioElement>('mlAudio'), msg = $('mlMsg');
  let cats: MusicCategory[] = [];
  let items: MusicInfo[] = [];
  let cat = '';
  let playing: string | null = null;
  let busy = false;
  let forceId: string | null = null;      // "ลบจริง" refused because works use it: the next tap confirms
  let picked: { file: File; durationMs: number } | null = null;

  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); };

  function paint() {
    const admin = h.isAdmin();
    const chips = $('mlChips');
    chips.textContent = '';
    const chip = (id: string, label: string) => {
      const b = document.createElement('button'); b.className = 'fchip'; b.textContent = label;
      b.setAttribute('aria-pressed', String(cat === id));
      b.onclick = () => { cat = id; paint(); };
      chips.append(b);
    };
    chip('', `ทั้งหมด ${items.length}`);
    for (const c of cats) { const n = items.filter(m => m.categoryId === c.id).length; if (n || admin) chip(c.id, `${c.name} ${n}`); }
    list.textContent = '';
    const shown = items.filter(m => !cat || m.categoryId === cat);
    if (!shown.length) list.append(Object.assign(document.createElement('li'), { className: 'empty', textContent: items.length ? 'ไม่มีเพลงในหมวดนี้' : admin || h.canUpload() || h.canAi() ? 'ยังไม่มีเพลงในคลัง เพิ่มเพลงด้านล่าง' : 'ยังไม่มีเพลงในคลัง ผู้ดูแลเป็นคนเพิ่ม' }));
    for (const m of shown) {
      const li = document.createElement('li'); li.className = 'mlrow' + (m.hidden ? ' mlhidden' : '');
      const body = document.createElement('div');
      const name = document.createElement('b'); name.textContent = m.name;
      const sub = document.createElement('small');
      sub.textContent = `${fmtTime(m.durationMs / 1000)} · ${cats.find(c => c.id === m.categoryId)?.name ?? ''} · ที่มา: ${m.source}`
        + (m.hidden ? ' · ซ่อนอยู่ (งานเดิมยังเล่นได้)' : '')
        + (admin && m.works !== undefined ? ` · ใช้ในงาน ${m.works} ชิ้น เทมเพลต ${m.templates ?? 0}` : '');
      body.append(name, sub);
      const acts = document.createElement('div'); acts.className = 'addrow';
      const play = document.createElement('button'); play.className = 'btn'; play.textContent = playing === m.id ? '■ หยุด' : '▶ ฟัง';
      play.onclick = () => listen(m);
      const use = document.createElement('button'); use.className = 'btn main'; use.textContent = 'ใช้เพลงนี้';
      use.disabled = busy || h.readonly() || m.hidden;
      use.onclick = () => void choose(m);
      acts.append(play, use);
      if (admin) {
        const hide = document.createElement('button'); hide.className = 'btn'; hide.textContent = m.hidden ? 'แสดง' : 'ซ่อน'; hide.disabled = busy;
        hide.onclick = () => void setHidden(m, !m.hidden);
        const del = document.createElement('button'); del.className = 'btn danger'; del.disabled = busy;
        del.textContent = forceId === m.id ? 'ยืนยันลบจริง' : 'ลบจริง';
        del.onclick = () => void remove(m);
        acts.append(hide, del);
      }
      body.append(acts);
      li.append(body);
      list.append(li);
    }
    const up = h.canUpload(), ai = h.canAi();
    $('mlAdmin').hidden = !(admin || up || ai);
    $('maBox').hidden = !ai;
    $('mlUpBox').hidden = !up;
    $('mlCatRow').hidden = !admin;
    if (up) {
      const sel = $<HTMLSelectElement>('mlCat');
      const keep = sel.value;
      sel.textContent = '';
      for (const c of cats) sel.append(new Option(c.name, c.id, false, c.id === keep));
      ($('mlUpload') as HTMLButtonElement).disabled = busy || !picked;
    }
  }

  async function load() {
    const off = h.online();
    if (off) { items = []; cats = []; say(`ยังเปิดคลังเพลงไม่ได้: ${off}`); paint(); return; }
    try {
      const r = await api.music();
      cats = r.categories; items = r.music;
      if (cat && !cats.some(c => c.id === cat)) cat = '';
      paint();
      if (h.canAi()) ai.repaint();
    } catch (e) { say('โหลดคลังเพลงไม่สำเร็จ: ' + errText(e)); }
  }

  const ai = initAiMusic({
    categories: () => cats, reload: load, videoSeconds: h.videoSeconds, online: h.online, isAdmin: h.isAdmin,
    stopOthers: () => { if (playing) { stop(); paint(); } }
  });

  function open() { say(''); forceId = null; void load(); if (h.canAi()) ai.open(); openSheet('shVMusicLib'); }

  function stop() { audio.pause(); audio.removeAttribute('src'); audio.load(); playing = null; }
  function listen(m: MusicInfo) {
    if (playing === m.id) { stop(); paint(); return; }
    stop(); ai.stopPlaying();
    playing = m.id;
    audio.src = m.file;
    audio.play().catch(e => { playing = null; say('เล่นตัวอย่างเพลงไม่ได้: ' + errText(e)); paint(); });
    paint();
  }
  audio.onended = () => { playing = null; paint(); };
  audio.onerror = () => { if (playing) { say('เปิดไฟล์เพลงนี้ไม่ได้ (อาจหายจากที่เก็บ)'); playing = null; paint(); } };
  // the panel closed: no sound keeps playing
  new MutationObserver(() => { if ($('shVMusicLib').hidden && playing) { stop(); } })
    .observe($('shVMusicLib'), { attributes: true, attributeFilter: ['hidden'] });

  async function choose(m: MusicInfo) {
    if (busy) return;
    busy = true; paint(); say(`กำลังดาวน์โหลด "${shortText(m.name, 40)}"…`);
    try { await h.use(m); say(`ใส่เพลง "${shortText(m.name, 40)}" แล้ว`, true); stop(); }
    catch (e) { say(errText(e)); }
    finally { busy = false; paint(); }
  }

  async function setHidden(m: MusicInfo, hidden: boolean) {
    busy = true; paint();
    try { await api.patchMusic(m.id, { hidden }); say(hidden ? `ซ่อน "${m.name}" แล้ว ไม่มีให้เลือกใหม่ แต่งานที่ใช้อยู่ยังเล่นได้` : `แสดง "${m.name}" ให้เลือกได้อีกครั้งแล้ว`, true); }
    catch (e) { say('เปลี่ยนไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; await load(); }
  }

  async function remove(m: MusicInfo) {
    busy = true; paint();
    const force = forceId === m.id;
    try {
      await api.deleteMusic(m.id, force);
      forceId = null;
      say(`ลบ "${m.name}" ออกจากคลังแล้ว`, true);
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 409 && !force) { forceId = m.id; say(errText(e) + ' · กด "ยืนยันลบจริง" อีกครั้งถ้าแน่ใจ'); }
      else { forceId = null; say('ลบไม่สำเร็จ: ' + errText(e)); }
    } finally { busy = false; await load(); }
  }

  /* ---------- admin: upload, categories ---------- */
  const fileIn = $<HTMLInputElement>('mlFile');
  $('mlPick').onclick = () => { fileIn.value = ''; fileIn.click(); };
  fileIn.onchange = async () => {
    const f = fileIn.files?.[0]; if (!f) return;
    picked = null; $('mlPicked').textContent = '';
    if (f.size > MAX_BYTES) { say(`ไฟล์ "${f.name}" ใหญ่ ${(f.size / 1048576).toFixed(1)} MB เกิน 15 MB`); paint(); return; }
    say('กำลังอ่านไฟล์เพลง…');
    try {
      const dur = await (await videoConvert()).probeMusic(f, f.name);
      picked = { file: f, durationMs: Math.round(dur * 1000) };
      $('mlPicked').textContent = `${f.name} · ${fmtTime(dur)} · ${(f.size / 1048576).toFixed(1)} MB`;
      if (!$in('mlName').value.trim()) $in('mlName').value = f.name.replace(/\.[^.]+$/, '').slice(0, 80);
      say('');
    } catch (e) { say(errText(e)); }
    paint();
  };
  $('mlUpload').onclick = async () => {
    if (!picked || busy) return;
    if (!$in('mlRights').checked) { say('ต้องติ๊กยืนยันว่ากลุ่มมีสิทธิ์ใช้เพลงนี้ก่อน'); return; }
    const source = $in('mlSource').value.trim();
    if (!source) { say('ต้องบอกที่มาของเพลง เช่น "ซื้อจาก …" หรือ "ฟรีลิขสิทธิ์จาก …"'); return; }
    busy = true; paint(); say(`กำลังอัปโหลด ${(picked.file.size / 1048576).toFixed(1)} MB…`);
    try {
      const r = await api.uploadMusic(picked.file, { name: $in('mlName').value, source, categoryId: $<HTMLSelectElement>('mlCat').value, durationMs: picked.durationMs });
      say(`เพิ่ม "${r.music.name}" เข้าคลังแล้ว ทุกคนในกลุ่มเลือกใช้ได้`, true);
      toast(`เพิ่มเพลง "${r.music.name}" เข้าคลังแล้ว`);
      picked = null; fileIn.value = ''; $('mlPicked').textContent = '';
      $in('mlName').value = ''; $in('mlSource').value = ''; $in('mlRights').checked = false;
    } catch (e) { say('อัปโหลดไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; await load(); }
  };
  $('mlAddCat').onclick = async () => {
    const name = $in('mlNewCat').value.trim();
    if (!name) { say('ใส่ชื่อหมวดก่อน'); return; }
    try { await api.addMusicCategory(name); $in('mlNewCat').value = ''; say(`เพิ่มหมวด "${name}" แล้ว`, true); await load(); }
    catch (e) { say('เพิ่มหมวดไม่สำเร็จ: ' + errText(e)); }
  };

  return { open };
}
